import { Seo } from "@/components/brand/Seo";
import {
  BorderBox,
  DarkSection,
  Eyebrow,
  GhostButton,
  OrangeButton,
  Section,
} from "@/components/brand/Bits";
import { AgencyPartners, AwardFeature, BrandGrid } from "@/components/brand/BrandWall";
import { ABOUT, POSITIONING, PRINCIPLES } from "@/data/siteContent";

export default function About() {
  return (
    <>
      <Seo
        path="/about"
        title="About AnyTribe — Creator Marketing Partner in Malaysia"
        description="AnyTribe is a Malaysian creator marketing partner with experience across retail, beauty, wellness, technology, food, travel and events, through direct-brand and agency-led collaborations."
      />

      <DarkSection testId="about-hero" className="pt-20 sm:pt-24">
        <Eyebrow dark>{POSITIONING}</Eyebrow>
        <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
          {ABOUT.title}
        </h1>
        <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">
          {ABOUT.paragraphs[0]}
        </p>
      </DarkSection>

      <Section testId="about-story">
        <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
          <div>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
              Who we are
            </h2>
            {ABOUT.paragraphs.slice(1).map((text) => (
              <p key={text} className="mt-5 text-base leading-relaxed body-text md:text-lg">
                {text}
              </p>
            ))}
            <div className="mt-8">
              <GhostButton to="/services" testId="about-services-link">
                Explore Our Services
              </GhostButton>
            </div>
          </div>
          <dl className="grid content-start gap-5 rounded-2xl border border-[#E6E1D8] bg-white p-6 sm:grid-cols-2 lg:grid-cols-1">
            {ABOUT.facts.map((fact) => (
              <div key={fact.label} data-testid={`about-fact-${fact.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <dt className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A8276]">
                  {fact.label}
                </dt>
                <dd className="mt-1.5 font-heading text-base font-extrabold">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Section>

      {/* Our approach */}
      <Section tone="cream" testId="principles-section">
        <div className="max-w-3xl">
          <Eyebrow>{PRINCIPLES.eyebrow}</Eyebrow>
          <h2 className="mt-5 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
            {PRINCIPLES.title}
          </h2>
          <p className="mt-5 text-base leading-relaxed body-text md:text-lg">{PRINCIPLES.body}</p>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {PRINCIPLES.items.map((item, index) => (
            <BorderBox
              key={item.title}
              testId={`principle-${String(index + 1).padStart(2, "0")}`}
            >
              <p className="font-heading text-3xl font-extrabold text-[#F2811D]">
                {String(index + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-4 font-heading text-lg font-extrabold">{item.title}</h3>
              <p className="mt-2.5 text-[15px] leading-relaxed body-text">{item.body}</p>
            </BorderBox>
          ))}
        </div>
        <p className="mt-9 max-w-3xl text-[15px] leading-relaxed body-text">{PRINCIPLES.extra}</p>
      </Section>

      <Section testId="about-brands">
        <BrandGrid />
        <AgencyPartners />
      </Section>

      <Section testId="about-award">
        <AwardFeature />
      </Section>

      <DarkSection testId="about-cta" tight>
        <div className="flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Let's Work Together
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/80">
              Tell us the objective, the window and a budget range. We'll come back with a creator
              mix and a cost per outcome.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <OrangeButton to="/contact" testId="about-cta-contact">
              Work With Us
            </OrangeButton>
            <GhostButton to="/services" dark testId="about-cta-services">
              Explore Our Services
            </GhostButton>
          </div>
        </div>
      </DarkSection>

    </>
  );
}
